import { App, Button, Checkbox, Drawer, Form, Input, Select, Space, TreeSelect } from 'antd';
import { useEffect, useMemo } from 'react';
import {
  isLeadershipViewerTier,
  LEADERSHIP_TIER,
  LEADERSHIP_TIER_OPTIONS,
} from '@/constants/leadership-tier';
import { USER_ROLES } from '@/constants/roles';
import { STATUS_LABELS, USER_STATUS } from '@/constants/user';
import { ApiError } from '@/services/api/client';
import * as usersApi from '@/services/api/users';
import type { DepartmentTreeNode } from '@/types/department';
import type { LeadershipTier, UserRecord } from '@/types/user';
import {
  findDepartmentNode,
  flattenDepartmentUsers,
  getLeaderOptionsFromParentDepartment,
  toParentTreeSelectData,
} from '@/utils/department-tree';

export type UserFormMode = 'create' | 'edit';

export interface UserFormValues {
  phone: string;
  password?: string;
  name: string;
  isSystemAdmin?: boolean;
  jobTitle?: string;
  status: UserRecord['status'];
  departmentId: string;
  leaderId?: string;
  isTopLeader?: boolean;
  leadershipTier: LeadershipTier;
}

interface UserFormDrawerProps {
  open: boolean;
  mode: UserFormMode;
  departmentTree: DepartmentTreeNode[];
  editingUser: UserRecord | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function UserFormDrawer({
  open,
  mode,
  departmentTree,
  editingUser,
  onClose,
  onSuccess,
}: UserFormDrawerProps) {
  const { message } = App.useApp();
  const [form] = Form.useForm<UserFormValues>();
  const isEdit = mode === 'edit';
  const isTopLeader = Form.useWatch('isTopLeader', form) ?? false;
  const leadershipTier = Form.useWatch('leadershipTier', form) as LeadershipTier | undefined;
  const departmentId = Form.useWatch('departmentId', form) as string | undefined;
  const shouldSkipLeader = isTopLeader || isLeadershipViewerTier(leadershipTier);

  const departmentTreeData = toParentTreeSelectData(departmentTree);

  const leaderOptions = useMemo(() => {
    const options = getLeaderOptionsFromParentDepartment(departmentTree, departmentId, {
      excludeUserId: editingUser?.id,
    });

    if (
      editingUser?.leaderId &&
      !options.some((option) => option.value === editingUser.leaderId)
    ) {
      const currentLeader = flattenDepartmentUsers(departmentTree).find(
        (user) => user.id === editingUser.leaderId,
      );
      if (currentLeader) {
        options.push({
          value: currentLeader.id,
          label: `${currentLeader.name}（${currentLeader.phone}）`,
        });
      }
    }

    return options;
  }, [departmentTree, departmentId, editingUser?.id, editingUser?.leaderId]);

  const selectedDepartment = departmentId
    ? findDepartmentNode(departmentTree, departmentId)
    : null;
  const parentDepartment = selectedDepartment?.parentId
    ? findDepartmentNode(departmentTree, selectedDepartment.parentId)
    : null;

  const leaderPlaceholder = useMemo(() => {
    if (shouldSkipLeader) {
      return leadershipTier === LEADERSHIP_TIER.LEADERSHIP_MEMBER
        ? '领导班子成员无需设置'
        : '该领导身份无需设置';
    }
    if (!departmentId) {
      return '请先选择所属部门';
    }
    if (!selectedDepartment?.parentId) {
      return '该部门无上级部门，请勾选顶层领导';
    }
    if (!parentDepartment) {
      return '未找到上级部门';
    }
    if (leaderOptions.length === 0) {
      return `上级部门「${parentDepartment.name}」暂无人员`;
    }
    return `请选择上级部门「${parentDepartment.name}」的人员`;
  }, [
    departmentId,
    leadershipTier,
    leaderOptions.length,
    parentDepartment,
    selectedDepartment?.parentId,
    shouldSkipLeader,
  ]);

  const statusOptions = Object.entries(STATUS_LABELS).map(([value, label]) => ({
    value,
    label,
  }));

  useEffect(() => {
    if (!open) {
      return;
    }

    if (isEdit && editingUser) {
      form.setFieldsValue({
        name: editingUser.name,
        isSystemAdmin: editingUser.role === USER_ROLES.ADMIN,
        jobTitle: editingUser.jobTitle ?? undefined,
        status: editingUser.status,
        departmentId: editingUser.departmentId,
        leaderId: editingUser.leaderId ?? undefined,
        isTopLeader: editingUser.isTopLeader,
        leadershipTier: editingUser.leadershipTier ?? LEADERSHIP_TIER.NONE,
      });
      return;
    }

    form.resetFields();
    form.setFieldsValue({
      status: USER_STATUS.ACTIVE,
      isSystemAdmin: false,
      isTopLeader: false,
      leadershipTier: LEADERSHIP_TIER.NONE,
    });
  }, [open, isEdit, editingUser, form]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const leaderId = form.getFieldValue('leaderId') as string | undefined;
    if (shouldSkipLeader) {
      if (leaderId) {
        form.setFieldValue('leaderId', undefined);
      }
      form.setFields([{ name: 'leaderId', errors: [] }]);
      return;
    }
    if (!departmentId) {
      return;
    }
    if (leaderId && !leaderOptions.some((option) => option.value === leaderId)) {
      form.setFieldValue('leaderId', undefined);
    }
  }, [departmentId, form, leaderOptions, open, shouldSkipLeader]);

  const handleDepartmentChange = (value: string | undefined) => {
    form.setFieldValue('departmentId', value);
    form.setFieldValue('leaderId', undefined);
  };

  const handleSubmit = async () => {
    const fieldNames: (keyof UserFormValues)[] = isEdit
      ? [
          'name',
          'password',
          'isSystemAdmin',
          'jobTitle',
          'status',
          'departmentId',
          'leaderId',
          'isTopLeader',
          'leadershipTier',
        ]
      : [
          'phone',
          'password',
          'name',
          'isSystemAdmin',
          'jobTitle',
          'status',
          'departmentId',
          'leaderId',
          'isTopLeader',
          'leadershipTier',
        ];
    const values = await form.validateFields(fieldNames);
    const jobTitle = values.jobTitle?.trim() || undefined;
    const topLeader = values.isTopLeader ?? false;
    const isSystemAdmin = values.isSystemAdmin ?? false;
    const skipLeader = topLeader || isLeadershipViewerTier(values.leadershipTier);

    try {
      if (isEdit && editingUser) {
        await usersApi.updateUser(editingUser.id, {
          name: values.name,
          password: values.password || undefined,
          isSystemAdmin,
          jobTitle,
          status: values.status,
          departmentId: values.departmentId,
          leaderId: skipLeader ? undefined : values.leaderId,
          isTopLeader: topLeader,
          leadershipTier: values.leadershipTier,
        });
        message.success('用户已更新');
      } else {
        await usersApi.createUser({
          phone: values.phone,
          password: values.password!,
          name: values.name,
          isSystemAdmin,
          jobTitle,
          status: values.status,
          departmentId: values.departmentId,
          leaderId: skipLeader ? undefined : values.leaderId,
          isTopLeader: topLeader,
          leadershipTier: values.leadershipTier,
        });
        message.success('用户已创建');
      }
      onSuccess();
      onClose();
    } catch (error) {
      const text =
        error instanceof ApiError
          ? error.message
          : error instanceof Error && error.message
            ? error.message
            : '保存失败';
      message.error(text);
    }
  };

  return (
    <Drawer
      title={isEdit ? '编辑用户' : '新增用户'}
      open={open}
      onClose={onClose}
      width={480}
      destroyOnClose
      footer={
        <Space style={{ float: 'right' }}>
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" onClick={() => void handleSubmit()}>
            保存
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" preserve={false}>
        {isEdit ? (
          <Form.Item label="手机号" extra="手机号作为登录账号，创建后不可修改">
            <Input value={editingUser?.phone ?? ''} disabled />
          </Form.Item>
        ) : (
          <Form.Item
            name="phone"
            label="手机号"
            rules={[
              { required: true, message: '请输入手机号' },
              { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确' },
            ]}
          >
            <Input placeholder="11 位手机号" maxLength={11} />
          </Form.Item>
        )}
        <Form.Item
          name="password"
          label={isEdit ? '重置密码' : '登录密码'}
          extra={
            isEdit
              ? '留空表示不修改；设置新密码后将解除微信绑定，用户需重新登录'
              : '用户使用手机号和该密码登录'
          }
          rules={[
            { required: !isEdit, message: '请设置登录密码' },
            { min: 6, message: '密码至少 6 位' },
            { max: 64, message: '密码最多 64 位' },
          ]}
        >
          <Input.Password
            placeholder={isEdit ? '输入新密码以重置' : '设置 6-64 位密码'}
            maxLength={64}
            autoComplete="new-password"
          />
        </Form.Item>
        <Form.Item
          name="name"
          label="姓名"
          rules={[
            { required: true, message: '请输入姓名' },
            { max: 64, message: '最多 64 个字符' },
          ]}
        >
          <Input placeholder="姓名" />
        </Form.Item>
        <Form.Item name="isSystemAdmin" valuePropName="checked">
          <Checkbox>系统管理员（可进入管理后台，不参与业务日报流程）</Checkbox>
        </Form.Item>
        <Form.Item name="jobTitle" label="职务" rules={[{ max: 64, message: '最多 64 个字符' }]}>
          <Input placeholder="选填，如局长、处长" />
        </Form.Item>
        <Form.Item name="status" label="状态" rules={[{ required: true, message: '请选择状态' }]}>
          <Select options={statusOptions} placeholder="请选择状态" />
        </Form.Item>
        <Form.Item
          name="departmentId"
          label="所属部门"
          rules={[{ required: true, message: '请选择部门' }]}
          extra="直属领导将列出该部门上一级部门的人员；是否有「团队日报」由是否存在直属下属自动决定"
        >
          <TreeSelect
            placeholder="请选择部门"
            treeData={departmentTreeData}
            treeDefaultExpandAll
            showSearch
            treeNodeFilterProp="title"
            onChange={handleDepartmentChange}
          />
        </Form.Item>
        <Form.Item name="isTopLeader" valuePropName="checked">
          <Checkbox>顶层领导（无直属上级，可提交日报但无审批人）</Checkbox>
        </Form.Item>
        <Form.Item
          name="leadershipTier"
          label="领导身份"
          rules={[{ required: true, message: '请选择领导身份' }]}
          extra="领导班子成员无需设置直属领导；局级单位负责人才选「单位负责人」；科室负责人选「无」。最高领导全系统仅可配置一人。"
        >
          <Select options={LEADERSHIP_TIER_OPTIONS} placeholder="请选择领导身份" />
        </Form.Item>
        <Form.Item
          name="leaderId"
          label="直属领导"
          extra={
            parentDepartment
              ? `候选来自上级部门：${parentDepartment.name}（${parentDepartment.code}）`
              : undefined
          }
          rules={[
            {
              validator: async (_, value: string | undefined) => {
                if (shouldSkipLeader) {
                  return;
                }
                if (!departmentId) {
                  throw new Error('请先选择所属部门');
                }
                if (!selectedDepartment?.parentId) {
                  throw new Error('该部门无上级部门，请勾选顶层领导');
                }
                if (!value) {
                  throw new Error('请选择直属领导');
                }
              },
            },
          ]}
        >
          <Select
            allowClear
            showSearch
            disabled={shouldSkipLeader || !departmentId || leaderOptions.length === 0}
            placeholder={leaderPlaceholder}
            optionFilterProp="label"
            options={leaderOptions}
            notFoundContent={leaderPlaceholder}
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
